import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import ModalBase from '@/components/Modal/ModalBase.jsx';
import { createCliente, searchClients } from '@/services/clients.js';
import { emitirEtiquetas } from '@/services/etiquetas.js';
import { getLocalPrintingPreferences } from '@/services/printing/directPrintService.js';
import { imprimirEtiquetas } from '@/services/printing/labelPrinting.js';
import { buildEtiquetaOrigen, toEtiquetaItemInput } from '@/modules/guias/etiquetaOrigen.js';
import {
  CargasPage,
  CargasTableShell,
  CargasViewport,
  cargasButtonClassName,
  cargasInputClassName,
  cargasPanelClassName,
  useOperatorCompact,
  useSaveShortcut,
} from './components/CargasLayout.jsx';

const MIN_VISIBLE_ROWS = 6;
const CLIENT_MEMORY_KEY = 'cargas:etiquetas:client-memory:v2';
const KEEP_DATA_KEY = 'cargas:etiquetas:keep-data:v1';

const EMPTY_ITEM = {
  id: '',
  punto_venta: '',
  numero_remito: '',
  remito: '',
  bultos: '',
  descripcion: '',
  kg_reales: '',
  kg_aforado: '',
  valor_declarado: '',
  kilos: '',
  kReal: '',
  mts3: '',
  m3: '',
};
const DEFAULT_CLIENT_FORM = {
  razonSocial: '',
  tipoDocumento: 'CUIT',
  cuit: '',
  condicionIVA: 'Responsable Inscripto',
  categoriaCliente: 'Transporte',
  direccion: '',
};

const HOTKEY_HINTS = [
  { key: 'F2', label: 'Remitente' },
  { key: 'F3', label: 'Destinatario' },
  { key: 'F4', label: 'Primer remito' },
  { key: 'F6', label: 'Cargar remito' },
  { key: 'F8', label: 'Emitir' },
  { key: 'Ctrl+Enter', label: 'Emitir' },
  { key: 'Esc', label: 'Cancelar foco' },
];

const SERVICIOS = [
  { codigo: 1, desc: 'Estandar' },
  { codigo: 2, desc: 'Express' },
  { codigo: 3, desc: 'Prioritario' },
  { codigo: 4, desc: 'Internacional' },
  { codigo: 5, desc: 'Especial' },
];

const numericFieldRules = {
  bultos: { decimals: 0 },
  kg_reales: { decimals: 3 },
  m3: { decimals: 4 },
  kg_aforado: { decimals: 3 },
  valor_declarado: { decimals: 2 },
};

const remitoDescriptionOptions = [
  'Bultos',
  'Cajas',
  'Pallets',
  'Mercaderia general',
];

const cardClassName =
  'rounded-2xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--bg),0.18)] p-3 shadow-[0_8px_24px_rgba(var(--shadow),0.06)]';

const denseInputClassName =
  'h-10 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--bg),0.58)] px-2.5 text-sm text-[rgb(var(--fg))] outline-none transition focus:ring-2 focus:ring-[rgb(var(--ring))]';

const denseMetaInputClassName =
  'h-10 rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--bg),0.58)] px-2.5 text-sm text-[rgb(var(--fg))] outline-none transition focus:ring-2 focus:ring-[rgb(var(--ring))]';

const quickButtonClassName =
  'inline-flex min-h-8 items-center justify-center rounded-full border border-[rgba(var(--border),0.55)] bg-[rgba(var(--bg),0.22)] px-2.5 py-1 text-xs font-medium text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--bg),0.36)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-45';

const emitButtonClassName =
  'inline-flex min-h-12 min-w-[220px] items-center justify-center rounded-2xl bg-[rgb(var(--brand))] px-5 py-3 text-left text-[rgb(var(--bg))] shadow-[0_12px_28px_rgba(var(--shadow),0.18)] transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-60';

function safeReadJson(key, fallback) {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function safeWriteJson(key, value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function safeReadBool(key, fallback) {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    return raw === '1';
  } catch {
    return fallback;
  }
}

function safeWriteBool(key, value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {}
}

function resolveSucursalId(sucursal, user) {
  return Number(sucursal?.id || sucursal?.sucursal_id || user?.sucursal_id || user?.sucursalId || 0);
}

function getPhone(value) {
  return value?.telefono || value?.telefono1 || value?.telefonoFijo || value?.celular || '';
}

function normalizeNumberInput(value) {
  return String(value ?? '')
    .replace(',', '.')
    .trim();
}

function parseNumber(value) {
  const parsed = Number(normalizeNumberInput(value));
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCurrency(value) {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
  }).format(Number(value || 0));
}

function normalizeNumericInput(value, decimals = 0) {
  const sanitized = String(value ?? '').replace(',', '.').replace(/[^\d.]/g, '');
  if (!sanitized) return '';
  const [integerPart, ...rest] = sanitized.split('.');
  const decimalPart = rest.join('').slice(0, decimals);
  if (!decimals) return integerPart;
  return decimalPart.length ? `${integerPart}.${decimalPart}` : integerPart;
}

function normalizeRemitoSegment(value, maxLength) {
  return String(value || '').replace(/\D/g, '').slice(0, maxLength);
}

function splitRemitoNumber(value) {
  const digits = normalizeRemitoSegment(value, 12);
  if (!digits) return { puntoVenta: '', numero: '' };
  if (digits.length <= 4) {
    return { puntoVenta: digits, numero: '' };
  }
  return {
    puntoVenta: digits.slice(0, 4),
    numero: digits.slice(4, 12),
  };
}

function buildRemitoNumber(puntoVenta, numero) {
  const normalizedPuntoVenta = normalizeRemitoSegment(puntoVenta, 4);
  const normalizedNumero = normalizeRemitoSegment(numero, 8);
  if (!normalizedPuntoVenta && !normalizedNumero) return '';
  return `${normalizedPuntoVenta.padStart(4, '0')}${normalizedNumero.padStart(8, '0')}`;
}

function cloneEmptyItem() {
  return { ...EMPTY_ITEM };
}

function normalizeItem(item = EMPTY_ITEM) {
  const fallbackParts = splitRemitoNumber(item.remito || item.numero_remito);
  const puntoVenta = String(item.punto_venta ?? fallbackParts.puntoVenta ?? '');
  const numeroRemito = String(item.numero_remito ?? fallbackParts.numero ?? '');
  const remitoCompleto = String(item.remito ?? buildRemitoNumber(puntoVenta, numeroRemito));
  return {
    id: String(item.id ?? remitoCompleto ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
    punto_venta: puntoVenta,
    numero_remito: numeroRemito,
    remito: remitoCompleto,
    bultos: item.bultos === 0 ? '0' : String(item.bultos ?? ''),
    descripcion: String(item.descripcion ?? ''),
    kg_reales: item.kg_reales === 0 ? '0' : String(item.kg_reales ?? item.kReal ?? item.kilos ?? ''),
    kg_aforado: item.kg_aforado === 0 ? '0' : String(item.kg_aforado ?? ''),
    valor_declarado: item.valor_declarado === 0 ? '0' : String(item.valor_declarado ?? ''),
    kReal: item.kReal === 0 ? '0' : String(item.kReal ?? item.kg_reales ?? ''),
    kilos: item.kilos === 0 ? '0' : String(item.kilos ?? item.kg_reales ?? item.kReal ?? ''),
    mts3: item.mts3 === 0 ? '0' : String(item.mts3 ?? item.m3 ?? ''),
    m3: item.m3 === 0 ? '0' : String(item.m3 ?? item.mts3 ?? ''),
  };
}

function isRowBlank(item) {
  return (
    !String(item?.remito ?? '').trim() &&
    !String(item?.numero_remito ?? '').trim() &&
    !String(item?.descripcion ?? '').trim() &&
    ['', '0'].includes(normalizeNumberInput(item?.bultos)) &&
    ['', '0'].includes(normalizeNumberInput(item?.kg_reales ?? item?.kilos)) &&
    ['', '0'].includes(normalizeNumberInput(item?.m3 ?? item?.mts3)) &&
    ['', '0'].includes(normalizeNumberInput(item?.kg_aforado)) &&
    ['', '0'].includes(normalizeNumberInput(item?.valor_declarado))
  );
}

function isMeaningfulItem(item) {
  return !isRowBlank(item);
}

function buildGridRows(source = []) {
  const base = Array.isArray(source) && source.length ? source.map(normalizeItem) : [cloneEmptyItem()];
  while (base.length < MIN_VISIBLE_ROWS) {
    base.push(cloneEmptyItem());
  }
  return base;
}

function normalizeClient(client) {
  if (!client) return null;
  const clienteId = client.clienteId || client.id || null;
  const nombre = client.nombre || client.razonSocial || client.nombreFantasia || client.razon || '';
  return {
    clienteId,
    id: clienteId,
    nombre,
    razonSocial: client.razonSocial || nombre,
    doc: client.doc || client.cuit || '',
    cuit: client.cuit || client.doc || '',
    direccion: client.direccion || '',
    telefono: getPhone(client),
    provinciaId: client.provinciaId || client.provincia?.id || null,
    provinciaNombre: client.provinciaNombre || client.provincia?.nombre || '',
    localidadId: client.localidadId || client.localidad?.id || null,
    localidadNombre: client.localidadNombre || client.localidad?.nombre || client.localidad || '',
    cp: client.cp || client.codigoPostal || '',
  };
}

function serializeEtiquetaClienteInput(client) {
  if (!client?.clienteId) return null;
  return {
    clienteId: client.clienteId,
    nombre: client.nombre || '',
    doc: client.doc || client.cuit || '',
    direccion: client.direccion || '',
    provinciaId: client.provinciaId ?? null,
    localidadId: client.localidadId ?? null,
    localidadNombre: client.localidadNombre || '',
    cp: client.cp || '',
  };
}

function serializeEtiquetaPagadorInput(client) {
  if (!client?.clienteId) return null;
  return {
    clienteId: client.clienteId,
    nombre: client.nombre || '',
  };
}

function buildClientSummary(client, sourceLabel) {
  if (!client?.clienteId) {
    return 'Escribi nombre, CUIT o localidad y Enter para seguir.';
  }
  const parts = [
    client.doc || 'Sin CUIT',
    client.localidadNombre || client.provinciaNombre || 'Sin localidad',
    client.direccion || 'Sin direccion',
  ];
  return sourceLabel ? `${sourceLabel} · ${parts.join(' · ')}` : parts.join(' · ');
}

function sanitizeItems(items) {
  return items
    .filter(isMeaningfulItem)
    .map(item => ({
      remito: String(item.remito ?? buildRemitoNumber(item.punto_venta, item.numero_remito)).trim(),
      bultos: parseNumber(item.bultos),
      descripcion: String(item.descripcion ?? '').trim(),
      kReal: parseNumber(item.kg_reales ?? item.kReal ?? item.kilos),
      kilos: parseNumber(item.kg_reales ?? item.kilos ?? item.kReal),
      mts3: parseNumber(item.m3 ?? item.mts3),
      kg_reales: parseNumber(item.kg_reales ?? item.kilos ?? item.kReal),
      m3: parseNumber(item.m3 ?? item.mts3),
      kg_aforado: parseNumber(item.kg_aforado),
      valor_declarado: parseNumber(item.valor_declarado),
      punto_venta: String(item.punto_venta ?? '').trim(),
      numero_remito: String(item.numero_remito ?? '').trim(),
    }));
}

function validateDraft({ remitente, destinatario, pagador, items }) {
  if (!remitente?.clienteId) return { message: 'Falta remitente', focus: { type: 'client', role: 'remitente' } };
  if (!destinatario?.clienteId) return { message: 'Falta destinatario', focus: { type: 'client', role: 'destinatario' } };
  if (!pagador?.clienteId) return { message: 'Falta pagador', focus: { type: 'client', role: 'pagador' } };

  const firstInvalidIndex = items.findIndex(item => {
    if (!isMeaningfulItem(item)) return false;
    if (!String(item.remito ?? '').trim()) return true;
    return parseNumber(item.bultos) <= 0;
  });

  if (firstInvalidIndex !== -1) {
    const row = items[firstInvalidIndex];
    if (!String(row.remito ?? '').trim()) {
      return {
        message: `Fila ${firstInvalidIndex + 1}: falta remito`,
        focus: { type: 'remito_pv' },
      };
    }
    return {
      message: `Fila ${firstInvalidIndex + 1}: indica bultos`,
      focus: { type: 'remito_pv' },
    };
  }

  const validItems = sanitizeItems(items);
  if (!validItems.length) {
    return { message: 'Carga al menos un remito', focus: { type: 'remito_pv' } };
  }

  return { validItems };
}

function downloadZpl(codigo, zpl) {
  if (!zpl) return;
  const blob = new Blob([zpl], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${codigo}.zpl.txt`;
  link.click();
  URL.revokeObjectURL(url);
}

function FieldMetric({ label, value }) {
  return (
    <div className="rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--bg),0.22)] px-3 py-2 text-sm">
      <span className="text-[rgba(var(--muted),0.92)]">{label}: </span>
      <span className="font-semibold text-[rgb(var(--fg))]">{value}</span>
    </div>
  );
}

function StepPill({ label, state }) {
  const isCurrent = state === 'current';
  const isDone = state === 'done';
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em]',
        isCurrent ? 'border-[rgba(var(--ring),0.72)] bg-[rgba(var(--brand),0.14)] text-[rgb(var(--fg))]' : null,
        isDone ? 'border-[rgba(var(--border),0.6)] bg-[rgba(var(--bg),0.24)] text-[rgb(var(--fg))]' : null,
        !isCurrent && !isDone
          ? 'border-[rgba(var(--border),0.35)] bg-transparent text-[rgba(var(--muted),0.92)]'
          : null,
      )}
    >
      {label}
    </span>
  );
}

function OperativeClientField({
  label,
  hotkey,
  value,
  onChange,
  onAdvance,
  inputRef,
  recentClients,
  lastClient,
  status,
  summaryPrefix,
  quickActions = [],
  searchCacheRef,
}) {
  const wrapperRef = useRef(null);
  const localInputRef = useRef(null);
  const quickNameRef = useRef(null);

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [message, setMessage] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isQuickOpen, setIsQuickOpen] = useState(false);
  const [quickForm, setQuickForm] = useState(DEFAULT_CLIENT_FORM);

  const effectiveQuery = query.trim();
  const selectedLabel = value?.nombre || value?.razonSocial || '';

  const recentResults = useMemo(() => {
    const list = [];

    const pushUnique = client => {
      const normalized = normalizeClient(client);
      if (!normalized?.clienteId) return;
      if (list.some(item => item.clienteId === normalized.clienteId)) return;
      list.push(normalized);
    };

    pushUnique(lastClient);
    (recentClients || []).forEach(pushUnique);
    return list.slice(0, 6);
  }, [lastClient, recentClients]);

  const visibleResults = effectiveQuery.length >= 2 ? results : recentResults;

  useEffect(() => {
    if (!isOpen) return undefined;

    if (effectiveQuery.length < 2) {
      setResults(recentResults);
      setLoading(false);
      setSelectedIndex(0);
      return undefined;
    }

    let active = true;
    const cacheKey = effectiveQuery.toLowerCase();
    const cached = searchCacheRef.current.get(cacheKey);

    if (cached) {
      setResults(cached);
      setLoading(false);
      setSelectedIndex(0);
      return undefined;
    }

    setLoading(true);
    setMessage('');

    searchClients({ search: effectiveQuery, page: 1, pageSize: 10 })
      .then(data => {
        if (!active) return;
        const normalized = (data?.data || []).map(normalizeClient).filter(Boolean);
        searchCacheRef.current.set(cacheKey, normalized);
        setResults(normalized);
        setSelectedIndex(0);
      })
      .catch(error => {
        if (!active) return;
        setResults([]);
        setMessage(error.message || 'No se pudo buscar.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [effectiveQuery, isOpen, recentResults, searchCacheRef]);

  useEffect(() => {
    const handlePointerDown = event => {
      if (!wrapperRef.current?.contains(event.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const handleSelect = client => {
    const normalized = normalizeClient(client);
    if (!normalized?.clienteId) return;
    onChange(normalized);
    setQuery('');
    setIsOpen(false);
    setMessage('');
    setSelectedIndex(0);
    onAdvance?.();
  };

  const handleAdvance = () => {
    if (!value?.clienteId) {
      setMessage(`Selecciona ${label.toLowerCase()}`);
      return;
    }
    setQuery('');
    setIsOpen(false);
    setMessage('');
    onAdvance?.();
  };

  const handleKeyDown = event => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      setSelectedIndex(prev => (prev < visibleResults.length - 1 ? prev + 1 : prev));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (visibleResults.length > 0 && (isOpen || effectiveQuery.length >= 2)) {
        handleSelect(visibleResults[selectedIndex] || visibleResults[0]);
        return;
      }
      handleAdvance();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (query) {
        setQuery('');
        setIsOpen(false);
        return;
      }
      localInputRef.current?.blur();
    }
  };

  const handleQuickSubmit = async event => {
    event.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const result = await createCliente({
        ...quickForm,
        razonSocial: quickForm.razonSocial,
        cuit: quickForm.cuit,
      });

      if (!result || result.success === false) {
        throw new Error(result?.message || 'No se pudo crear el cliente.');
      }

      const created = result.createCliente || result;
      handleSelect({
        id: created.id,
        razonSocial: created.razonSocial || quickForm.razonSocial,
        cuit: quickForm.cuit,
        direccion: quickForm.direccion,
      });
      setIsQuickOpen(false);
      setQuickForm(DEFAULT_CLIENT_FORM);
    } catch (error) {
      setMessage(error.message || 'No se pudo crear el cliente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={clsx(
        cardClassName,
        'relative overflow-visible',
        status === 'current' ? 'border-[rgba(var(--ring),0.72)] ring-2 ring-[rgba(var(--ring),0.18)]' : null,
      )}
      ref={wrapperRef}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <h2 className="text-sm font-semibold text-[rgb(var(--fg))]">{label}</h2>
          <span className="rounded-full border border-[rgba(var(--border),0.45)] bg-[rgba(var(--bg),0.24)] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgba(var(--muted),0.92)]">
            {hotkey}
          </span>
        </div>

        <button
          type="button"
          className={quickButtonClassName}
          tabIndex={-1}
          onMouseDown={event => event.preventDefault()}
          onClick={() => setIsQuickOpen(true)}
        >
          Alta
        </button>
      </div>

      <div className="relative mt-2">
        <input
          ref={node => {
            localInputRef.current = node;
            if (inputRef) inputRef.current = node;
          }}
          type="text"
          value={query || selectedLabel}
          placeholder={`Buscar ${label.toLowerCase()}...`}
          className={clsx(denseInputClassName, 'pr-14')}
          onFocus={event => {
            setIsOpen(true);
            setMessage('');
            if (!query && selectedLabel) {
              event.currentTarget.select();
            }
          }}
          onChange={event => {
            setQuery(event.target.value);
            setIsOpen(true);
            setMessage('');
            setSelectedIndex(0);
          }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          spellCheck={false}
        />

        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgba(var(--muted),0.88)]">
          {loading ? 'Buscando' : 'Enter'}
        </div>

        {isOpen ? (
          <div className="absolute left-0 right-0 top-full z-30 mt-2 max-h-64 overflow-y-auto rounded-2xl border border-[rgba(var(--border),0.58)] bg-[rgb(var(--panel))] shadow-[0_18px_40px_rgba(var(--shadow),0.18)]">
            {loading ? (
              <div className="px-3 py-3 text-sm text-[rgba(var(--muted),0.92)]">Buscando clientes...</div>
            ) : null}

            {!loading && visibleResults.length > 0 ? (
              <ul className="divide-y divide-[rgba(var(--border),0.42)]">
                {visibleResults.map((item, index) => (
                  <li key={`${label}-${item.clienteId}-${index}`}>
                    <button
                      type="button"
                      className={clsx(
                        'flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition',
                        index === selectedIndex ? 'bg-[rgba(var(--brand),0.12)]' : 'hover:bg-[rgba(var(--bg),0.28)]',
                      )}
                      onMouseDown={event => event.preventDefault()}
                      onClick={() => handleSelect(item)}
                    >
                      <span className="truncate text-sm font-semibold text-[rgb(var(--fg))]">
                        {item.nombre || item.razonSocial}
                      </span>
                      <span className="truncate text-xs text-[rgba(var(--muted),0.92)]">
                        {buildClientSummary(item)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            {!loading && visibleResults.length === 0 ? (
              <div className="px-3 py-3 text-sm text-[rgba(var(--muted),0.92)]">
                {effectiveQuery.length >= 2 ? 'Sin coincidencias.' : 'Ultimos usados y busqueda rapida.'}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="mt-2 rounded-xl border border-[rgba(var(--border),0.4)] bg-[rgba(var(--bg),0.16)] px-2.5 py-2 text-xs text-[rgba(var(--muted),0.94)]">
        {buildClientSummary(value, summaryPrefix)}
      </div>

      {quickActions.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {quickActions.map(action => (
            <button
              key={action.label}
              type="button"
              tabIndex={-1}
              className={quickButtonClassName}
              disabled={action.disabled}
              onMouseDown={event => event.preventDefault()}
              onClick={action.onClick}
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}

      {message ? (
        <p className="mt-2 text-xs font-semibold text-[rgb(var(--brand-700))]" role="alert">
          {message}
        </p>
      ) : null}

      <ModalBase
        isOpen={isQuickOpen}
        onClose={() => setIsQuickOpen(false)}
        labelledBy={`${label}-alta-rapida`}
        initialFocusRef={quickNameRef}
      >
        <form className="space-y-4" onSubmit={handleQuickSubmit}>
          <div>
            <h3 id={`${label}-alta-rapida`} className="text-lg font-semibold text-[rgb(var(--fg))]">
              Alta rapida de {label.toLowerCase()}
            </h3>
            <p className="mt-1 text-sm text-[rgba(var(--muted),0.92)]">
              Completa solo lo minimo y queda seleccionado.
            </p>
          </div>

          {message ? <p className="text-sm text-[rgb(var(--brand-700))]">{message}</p> : null}

          <div className="grid gap-3 md:grid-cols-2">
            <input
              ref={quickNameRef}
              required
              type="text"
              value={quickForm.razonSocial}
              onChange={event => setQuickForm(prev => ({ ...prev, razonSocial: event.target.value }))}
              placeholder="Razon social"
              className={cargasInputClassName}
            />
            <input
              required
              type="text"
              value={quickForm.cuit}
              onChange={event => setQuickForm(prev => ({ ...prev, cuit: event.target.value }))}
              placeholder="CUIT"
              className={cargasInputClassName}
            />
            <input
              type="text"
              value={quickForm.direccion}
              onChange={event => setQuickForm(prev => ({ ...prev, direccion: event.target.value }))}
              placeholder="Direccion"
              className={clsx(cargasInputClassName, 'md:col-span-2')}
            />
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" className={cargasButtonClassName} onClick={() => setIsQuickOpen(false)}>
              Cancelar
            </button>
            <button type="submit" className={cargasButtonClassName} disabled={loading}>
              {loading ? 'Guardando...' : 'Crear y seguir'}
            </button>
          </div>
        </form>
      </ModalBase>
    </div>
  );
}

export default function EtiquetasNueva() {
  const location = useLocation();
  const navigate = useNavigate();
  const { sucursalActiva, sucursal, user, tenant } = useAuth();
  const prefill = location.state?.prefill || {};
  const { isOperatorCompact } = useOperatorCompact();

  const defaultSucursalId = resolveSucursalId(sucursalActiva || sucursal, user);
  const defaultUsuarioId = Number(user?.id || user?.user_id || 0);

  const [servicio, setServicio] = useState(SERVICIOS[0]);
  const [remitente, setRemitente] = useState(() => normalizeClient(prefill.remitente));
  const [destinatario, setDestinatario] = useState(() => normalizeClient(prefill.destinatario));
  const [pagador, setPagador] = useState(() => normalizeClient(prefill.pagador));
  const [pagadorSource, setPagadorSource] = useState(() => {
    const initialPagador = normalizeClient(prefill.pagador);
    const initialRemitente = normalizeClient(prefill.remitente);
    const initialDestinatario = normalizeClient(prefill.destinatario);

    if (initialPagador?.clienteId && initialPagador?.clienteId === initialRemitente?.clienteId) {
      return 'remitente';
    }
    if (initialPagador?.clienteId && initialPagador?.clienteId === initialDestinatario?.clienteId) {
      return 'destinatario';
    }
    return null;
  });
  const [items, setItems] = useState(() => buildGridRows(prefill.items));
  const [copiasTotal, setCopiasTotal] = useState(prefill.copiasTotal || 1);
  const [valorDeclarado, setValorDeclarado] = useState(prefill.valorDeclarado || 0);
  const [remitoDraft, setRemitoDraft] = useState(() => cloneEmptyItem());
  const [remitoErrors, setRemitoErrors] = useState({});
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [editingRemitoDraft, setEditingRemitoDraft] = useState(() => cloneEmptyItem());
  const [highlightRemitoId, setHighlightRemitoId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emisiones, setEmisiones] = useState([]);
  const [lastLote, setLastLote] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [copiesEdited, setCopiesEdited] = useState(Boolean(prefill.copiasTotal));
  const [keepDataForNext, setKeepDataForNext] = useState(() => safeReadBool(KEEP_DATA_KEY, true));
  const [clientMemory, setClientMemory] = useState(() =>
    safeReadJson(CLIENT_MEMORY_KEY, { recent: [], byRole: {} }),
  );
  const [focusRequest, setFocusRequest] = useState(null);

  const sucursalId = prefill.sucursalId || defaultSucursalId || '';
  const usuarioId = prefill.usuarioId || defaultUsuarioId || '';
  const recepcionId = prefill.recepcionId || '';

  const remitenteInputRef = useRef(null);
  const destinatarioInputRef = useRef(null);
  const pagadorInputRef = useRef(null);
  const remitoPuntoVentaInputRef = useRef(null);
  const remitoNumeroInputRef = useRef(null);
  const copiesInputRef = useRef(null);
  const valorInputRef = useRef(null);
  const serviceSelectRef = useRef(null);
  const emitButtonRef = useRef(null);
  const searchCacheRef = useRef(new Map());

  useEffect(() => {
    safeWriteBool(KEEP_DATA_KEY, keepDataForNext);
  }, [keepDataForNext]);

  useEffect(() => {
    safeWriteJson(CLIENT_MEMORY_KEY, clientMemory);
  }, [clientMemory]);

  useEffect(() => {
    if (!focusRequest) return undefined;

    const frame = window.requestAnimationFrame(() => {
      if (focusRequest.type === 'client') {
        const refs = {
          remitente: remitenteInputRef,
          destinatario: destinatarioInputRef,
          pagador: pagadorInputRef,
        };
        refs[focusRequest.role]?.current?.focus({ preventScroll: true });
        refs[focusRequest.role]?.current?.select?.();
      }

      if (focusRequest.type === 'remito_pv') {
        remitoPuntoVentaInputRef.current?.focus({ preventScroll: true });
        remitoPuntoVentaInputRef.current?.select?.();
      }

      if (focusRequest.type === 'emit') {
        emitButtonRef.current?.focus({ preventScroll: true });
      }

      if (focusRequest.type === 'copies') {
        copiesInputRef.current?.focus({ preventScroll: true });
        copiesInputRef.current?.select?.();
      }

      if (focusRequest.type === 'valor') {
        valorInputRef.current?.focus({ preventScroll: true });
        valorInputRef.current?.select?.();
      }

      if (focusRequest.type === 'servicio') {
        serviceSelectRef.current?.focus({ preventScroll: true });
      }

      setFocusRequest(null);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [focusRequest, items.length]);

  useEffect(() => {
    if (prefill.remitente || prefill.destinatario || prefill.pagador || prefill.items?.length) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setFocusRequest({ type: 'client', role: 'remitente', token: Date.now() });
    }, 10);

    return () => window.clearTimeout(timer);
  }, [prefill.destinatario, prefill.items?.length, prefill.pagador, prefill.remitente]);

  useEffect(() => {
    if (!highlightRemitoId) return undefined;
    const timer = window.setTimeout(() => setHighlightRemitoId(null), 900);
    return () => window.clearTimeout(timer);
  }, [highlightRemitoId]);

  const filledItems = useMemo(() => sanitizeItems(items), [items]);

  const totals = useMemo(() => {
    const bultos = filledItems.reduce((acc, item) => acc + Number(item.bultos || 0), 0);
    const kilos = filledItems.reduce((acc, item) => acc + Number(item.kg_reales || item.kilos || item.kReal || 0), 0);
    const mts3 = filledItems.reduce((acc, item) => acc + Number(item.m3 || item.mts3 || 0), 0);
    const kgAforado = filledItems.reduce((acc, item) => acc + Number(item.kg_aforado || 0), 0);
    const valorDeclaradoTotal = filledItems.reduce((acc, item) => acc + Number(item.valor_declarado || 0), 0);
    return {
      cantidad: filledItems.length,
      bultos,
      kilos,
      kgReales: kilos,
      mts3,
      m3: mts3,
      kgAforado,
      valorDeclarado: valorDeclaradoTotal,
    };
  }, [filledItems]);

  useEffect(() => {
    setValorDeclarado(totals.valorDeclarado || 0);
  }, [totals.valorDeclarado]);

  // One label per bulto; the copies field only applies when no bultos were declared.
  const labelsFollowBultos = Number(totals.bultos || 0) > 0;
  const suggestedCopies = useMemo(() => Math.max(1, Number(totals.bultos || 0)), [totals.bultos]);

  const resolvedCopies = useMemo(() => {
    if (copiesEdited && !labelsFollowBultos) {
      return Math.max(1, Number(copiasTotal || 0) || 1);
    }
    return suggestedCopies;
  }, [copiesEdited, copiasTotal, labelsFollowBultos, suggestedCopies]);

  const currentStep = useMemo(() => {
    if (!remitente?.clienteId) return 'remitente';
    if (!destinatario?.clienteId) return 'destinatario';
    if (!pagador?.clienteId) return 'pagador';
    if (!filledItems.length) return 'remitos';
    return 'emitir';
  }, [destinatario, filledItems.length, pagador, remitente]);

  const rememberClient = (role, client) => {
    const normalized = normalizeClient(client);
    if (!normalized?.clienteId) return;

    setClientMemory(prev => ({
      recent: [normalized, ...(prev.recent || []).filter(item => item.clienteId !== normalized.clienteId)].slice(0, 8),
      byRole: {
        ...(prev.byRole || {}),
        [role]: normalized,
      },
    }));
  };

  const ensureVisibleRows = nextItems => buildGridRows(nextItems);

  const validateRemitoEntry = source => {
    const payload = normalizeItem(source);
    const errors = {};
    const remitoCompleto = buildRemitoNumber(payload.punto_venta, payload.numero_remito);

    if (!remitoCompleto) {
      errors.numero_remito = 'El número de remito es obligatorio.';
    }
    if (parseNumber(payload.bultos) < 0) {
      errors.bultos = 'Bultos debe ser mayor o igual a 0.';
    }
    if (parseNumber(payload.kg_reales) < 0) {
      errors.kg_reales = 'Kg reales debe ser mayor o igual a 0.';
    }
    if (parseNumber(payload.m3) < 0) {
      errors.m3 = 'M3 debe ser mayor o igual a 0.';
    }
    if (parseNumber(payload.kg_aforado) < 0) {
      errors.kg_aforado = 'Kg aforado debe ser mayor o igual a 0.';
    }
    if (parseNumber(payload.valor_declarado) < 0) {
      errors.valor_declarado = 'Valor declarado debe ser mayor o igual a 0.';
    }
    if (!String(payload.descripcion || '').trim()) {
      errors.descripcion = 'La descripcion del remito es obligatoria.';
    }

    return {
      ok: Object.keys(errors).length === 0,
      errors,
      value: {
        ...payload,
        id: payload.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        remito: remitoCompleto,
        kilos: payload.kg_reales,
        kReal: payload.kg_reales,
        mts3: payload.m3,
      },
    };
  };

  const handleDraftChange = (field, value) => {
    if (remitoErrors[field]) {
      setRemitoErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }

    if (numericFieldRules[field]) {
      const normalized = normalizeNumericInput(value, numericFieldRules[field].decimals);
      setRemitoDraft(prev => ({ ...prev, [field]: normalized }));
      return;
    }

    setRemitoDraft(prev => ({ ...prev, [field]: value }));
  };

  const handlePuntoVentaChange = value => {
    const nextPuntoVenta = normalizeRemitoSegment(value, 4);
    handleDraftChange('punto_venta', nextPuntoVenta);
    if (nextPuntoVenta.length === 4) {
      requestAnimationFrame(() => remitoNumeroInputRef.current?.focus?.());
    }
  };

  const handleNumeroRemitoChange = value => {
    const nextNumero = normalizeRemitoSegment(value, 8);
    handleDraftChange('numero_remito', nextNumero);
    if (nextNumero.length === 8) {
      requestAnimationFrame(() => document.getElementById('etiquetas-bultos')?.focus?.());
    }
  };

  const handleRemitoDraftBlur = () => {
    setRemitoDraft(prev => ({
      ...prev,
      punto_venta: prev.punto_venta ? normalizeRemitoSegment(prev.punto_venta, 4).padStart(4, '0') : '',
      numero_remito: prev.numero_remito ? normalizeRemitoSegment(prev.numero_remito, 8).padStart(8, '0') : '',
    }));
  };

  const handleEditingDraftChange = (field, value) => {
    if (numericFieldRules[field]) {
      const normalized = normalizeNumericInput(value, numericFieldRules[field].decimals);
      setEditingRemitoDraft(prev => ({ ...prev, [field]: normalized }));
      return;
    }
    setEditingRemitoDraft(prev => ({ ...prev, [field]: value }));
  };

  const handleAddRemito = () => {
    const result = validateRemitoEntry(remitoDraft);
    if (!result.ok) {
      setRemitoErrors(result.errors);
      return;
    }

    setItems(prev => ensureVisibleRows([...prev.filter(item => !isRowBlank(item)), result.value]));
    setRemitoErrors({});
    setRemitoDraft(cloneEmptyItem());
    setHighlightRemitoId(result.value.id);
    requestAnimationFrame(() => remitoPuntoVentaInputRef.current?.focus?.());
  };

  const startEditingRemito = (index, remito) => {
    const parts = splitRemitoNumber(remito?.remito || remito?.numero_remito);
    setEditingRowIndex(index);
    setEditingRemitoDraft({
      ...cloneEmptyItem(),
      ...normalizeItem(remito),
      punto_venta: parts.puntoVenta,
      numero_remito: parts.numero,
    });
    setRemitoErrors({});
  };

  const cancelEditingRemito = () => {
    setEditingRowIndex(null);
    setEditingRemitoDraft(cloneEmptyItem());
    setRemitoErrors({});
  };

  const saveEditingRemito = () => {
    if (editingRowIndex === null) return;
    const currentItem = items[editingRowIndex];
    const result = validateRemitoEntry({ ...currentItem, ...editingRemitoDraft, id: currentItem?.id });
    if (!result.ok) {
      setRemitoErrors(result.errors);
      return;
    }
    setItems(prev => ensureVisibleRows(prev.map((item, index) => (index === editingRowIndex ? result.value : item))));
    cancelEditingRemito();
  };

  const duplicateRemito = index => {
    const source = items[index];
    if (!source || isRowBlank(source)) return;
    const parts = splitRemitoNumber(source.remito || source.numero_remito);
    const duplicated = normalizeItem({
      ...source,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      punto_venta: parts.puntoVenta,
      numero_remito: parts.numero,
    });
    setRemitoDraft(duplicated);
    setRemitoErrors({});
    requestAnimationFrame(() => remitoNumeroInputRef.current?.focus?.());
  };

  const removeRow = rowIndex => {
    setItems(prev => {
      const next = prev.filter((_, index) => index !== rowIndex);
      return ensureVisibleRows(next.length ? next : [cloneEmptyItem()]);
    });
  };

  const focusFirstOpenRow = () => {
    setFocusRequest({ type: 'remito_pv', token: Date.now() });
  };

  const assignPagadorFrom = source => {
    const candidate = source === 'destinatario' ? destinatario : remitente;
    if (!candidate?.clienteId) return;
    setPagador(candidate);
    setPagadorSource(source);
    rememberClient('pagador', candidate);
    focusFirstOpenRow();
  };

  const handleRemitenteChange = client => {
    const normalized = normalizeClient(client);
    setRemitente(normalized);
    rememberClient('remitente', normalized);

    if (!pagador?.clienteId || pagadorSource === 'remitente') {
      setPagador(normalized);
      setPagadorSource('remitente');
      rememberClient('pagador', normalized);
    }
  };

  const handleDestinatarioChange = client => {
    const normalized = normalizeClient(client);
    setDestinatario(normalized);
    rememberClient('destinatario', normalized);

    if (pagadorSource === 'destinatario') {
      setPagador(normalized);
      rememberClient('pagador', normalized);
    }
  };

  const handlePagadorChange = client => {
    const normalized = normalizeClient(client);
    setPagador(normalized);
    setPagadorSource('manual');
    rememberClient('pagador', normalized);
  };

  const clearForNextLoad = keepData => {
    setItems(buildGridRows([]));
    setCopiesEdited(false);
    setCopiasTotal(1);
    setValorDeclarado(0);

    if (keepData) {
      setFocusRequest({ type: 'remito_pv', token: Date.now() });
      return;
    }

    setRemitente(null);
    setDestinatario(null);
    setPagador(null);
    setPagadorSource(null);
    setFocusRequest({ type: 'client', role: 'remitente', token: Date.now() });
  };

  const printLote = async (labels, origin) => {
    if (!labels?.length) return;
    try {
      const result = await imprimirEtiquetas(labels, { scope: { user, tenant } });
      if (result.failed?.length) {
        setFeedback({
          type: 'warning',
          message: `Se enviaron ${result.count} de ${labels.length} etiquetas. No se pudieron enviar: ${result.failed.map(item => item.codigoEtiqueta || item.id).join(', ')}. Reimprimilas desde el listado de etiquetas.`,
          origin,
        });
        return;
      }
      setFeedback({ type: 'success', message: `${result.count} etiqueta${result.count === 1 ? '' : 's'} enviada${result.count === 1 ? '' : 's'} a ${result.printerName}.`, origin });
    } catch (printError) {
      setFeedback({
        type: 'warning',
        message: `Las etiquetas están emitidas, pero no se pudieron imprimir. ${printError?.message || 'Revisá Sinamon Print Agent y la impresora configurada.'}`,
        origin,
      });
    }
  };

  const emitirGuiaDesdeLote = () => {
    if (!lastLote) return;
    navigate('/guias/emitir', { state: { etiquetaOrigen: lastLote } });
  };

  const handleEmitir = async origin => {
    const validation = validateDraft({ remitente, destinatario, pagador, items });
    if (!validation.validItems) {
      setFeedback({ type: 'error', message: validation.message, origin });
      setFocusRequest({ ...validation.focus, token: Date.now() });
      return { ok: false, errors: [validation.message] };
    }

    setLoading(true);
    setFeedback(null);

    try {
      const printingPreferences = getLocalPrintingPreferences({ user, tenant });
      const payload = {
        recepcionId: recepcionId || null,
        sucursalId: Number(sucursalId) || null,
        usuarioId: Number(usuarioId) || null,
        servicioCodigo: servicio.codigo,
        servicioDesc: servicio.desc,
        pagador: serializeEtiquetaPagadorInput(pagador),
        remitente: serializeEtiquetaClienteInput(remitente),
        destinatario: serializeEtiquetaClienteInput(destinatario),
        items: validation.validItems.map(toEtiquetaItemInput),
        valorDeclarado: Number(valorDeclarado || 0),
        copiasTotal: resolvedCopies,
        dpi: printingPreferences.labelDpi,
      };

      const data = await emitirEtiquetas(payload);

      const prepared = data || [];
      setEmisiones(prepared);
      setLastLote(buildEtiquetaOrigen({ labels: prepared, items: validation.validItems, remitente, destinatario }));
      setFeedback({
        type: 'success',
        message: `${prepared.length} etiqueta${prepared.length === 1 ? '' : 's'} emitida${prepared.length === 1 ? '' : 's'} (una por bulto). ${keepDataForNext ? 'Listo para seguir.' : 'Carga terminada.'}`,
        origin,
      });

      clearForNextLoad(keepDataForNext);

      if (printingPreferences.autoPrintLabels) {
        void printLote(prepared, 'automatic-print');
      }
      return { ok: true };
    } catch (error) {
      const message = error.message || 'No se pudieron emitir las etiquetas.';
      setFeedback({ type: 'error', message, origin });
      return { ok: false, errors: [message] };
    } finally {
      setLoading(false);
    }
  };

  useSaveShortcut(() => handleEmitir('hotkey:Ctrl+S'));

  useEffect(() => {
    const onKeyDown = event => {
      const hasOpenDialog = Boolean(document.querySelector('[role="dialog"][aria-modal="true"]'));
      if (hasOpenDialog && event.key !== 'Escape') {
        return;
      }

      if (event.key === 'F2') {
        event.preventDefault();
        setFocusRequest({ type: 'client', role: 'remitente', token: Date.now() });
        return;
      }

      if (event.key === 'F3') {
        event.preventDefault();
        setFocusRequest({ type: 'client', role: 'destinatario', token: Date.now() });
        return;
      }

      if (event.key === 'F4') {
        event.preventDefault();
        focusFirstOpenRow();
        return;
      }

      if (event.key === 'F6') {
        event.preventDefault();
        focusFirstOpenRow();
        return;
      }

      if (event.key === 'F8') {
        event.preventDefault();
        void handleEmitir('hotkey:F8');
        return;
      }

      if (event.key === 'F9' && emisiones.length) {
        event.preventDefault();
        void printLote(emisiones, 'hotkey:F9');
        return;
      }

      if (event.key === 'F10' && lastLote) {
        event.preventDefault();
        emitirGuiaDesdeLote();
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        void handleEmitir('hotkey:Ctrl+Enter');
        return;
      }

      if (event.key === 'Escape') {
        const active = document.activeElement;
        if (active instanceof HTMLElement && active !== document.body) {
          active.blur();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleEmitir, items.length, emisiones, lastLote]);

  const footer = (
    <div className="sticky bottom-0 z-20">
      <div
        className={clsx(
          cargasPanelClassName,
          'flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 backdrop-blur',
        )}
      >
        <div className="flex flex-wrap items-center gap-2">
          <FieldMetric label="Remitos" value={filledItems.length} />
          <FieldMetric label="Bultos" value={totals.bultos} />
          <FieldMetric label="Kilos" value={totals.kilos} />

          <label className="flex min-h-10 items-center gap-2 rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--bg),0.22)] px-3 py-2 text-sm text-[rgb(var(--fg))]">
            <input
              type="checkbox"
              checked={keepDataForNext}
              onChange={event => setKeepDataForNext(event.target.checked)}
              style={{ accentColor: 'rgb(var(--brand))' }}
            />
            Mantener datos para siguiente carga
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--bg),0.22)] px-3 py-2 text-sm text-[rgb(var(--fg))]">
            <span className="text-[rgba(var(--muted),0.92)]">Servicio</span>
            <select
              ref={serviceSelectRef}
              value={servicio.codigo}
              onChange={event => {
                const next = SERVICIOS.find(item => String(item.codigo) === event.target.value) || SERVICIOS[0];
                setServicio(next);
              }}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  setFocusRequest({ type: 'copies', token: Date.now() });
                }
              }}
              className={denseMetaInputClassName}
            >
              {SERVICIOS.map(item => (
                <option key={item.codigo} value={item.codigo}>
                  {item.desc}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--bg),0.22)] px-3 py-2 text-sm text-[rgb(var(--fg))]">
            <span className="text-[rgba(var(--muted),0.92)]">Etiquetas</span>
            <input
              ref={copiesInputRef}
              type="number"
              disabled={labelsFollowBultos}
              title={labelsFollowBultos ? 'Se emite una etiqueta por bulto (1/N … N/N).' : 'Cantidad de etiquetas cuando no se declararon bultos.'}
              value={copiesEdited && !labelsFollowBultos ? copiasTotal : resolvedCopies}
              onChange={event => {
                setCopiesEdited(true);
                setCopiasTotal(event.target.value);
              }}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  setFocusRequest({ type: 'valor', token: Date.now() });
                }
              }}
              className={clsx(denseMetaInputClassName, 'w-20')}
            />
          </label>

          <label className="flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--bg),0.22)] px-3 py-2 text-sm text-[rgb(var(--fg))]">
            <span className="text-[rgba(var(--muted),0.92)]">Valor decl.</span>
            <input
              ref={valorInputRef}
              type="number"
              value={valorDeclarado}
              readOnly
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  setFocusRequest({ type: 'emit', token: Date.now() });
                }
              }}
              className={clsx(denseMetaInputClassName, 'w-28')}
            />
          </label>

          <button type="button" className={cargasButtonClassName} onClick={() => navigate('/cargas/etiquetas')}>
            Listado
          </button>

          <button
            ref={emitButtonRef}
            type="button"
            className={emitButtonClassName}
            onClick={() => handleEmitir('button')}
            disabled={loading}
          >
            <span className="flex flex-col leading-tight">
              <span className="text-base font-semibold">{loading ? 'Emitiendo...' : 'Emitir etiquetas'}</span>
              <span className="text-xs font-medium text-[rgba(var(--bg),0.82)]">F8 o Ctrl+Enter</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <CargasPage
      title="Emision de etiquetas"
      description="Carga rapida para deposito y rampa."
      compactOverride={isOperatorCompact}
      hideHeaderOnCompact
      footer={footer}
    >
      <CargasViewport className="gap-2 overflow-visible">
        <section className={clsx(cargasPanelClassName, 'relative z-20 shrink-0 overflow-visible px-3 py-3')}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-sm font-semibold text-[rgb(var(--fg))]">Carga rapida de etiquetas</h1>
                {recepcionId ? (
                  <span className="rounded-full border border-[rgba(var(--border),0.45)] bg-[rgba(var(--bg),0.24)] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgba(var(--muted),0.92)]">
                    Recepcion {recepcionId}
                  </span>
                ) : null}
                <span className="rounded-full border border-[rgba(var(--border),0.45)] bg-[rgba(var(--bg),0.24)] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgba(var(--muted),0.92)]">
                  Sucursal {sucursalId || 'N/D'}
                </span>
              </div>

              <div className="mt-2 flex flex-wrap gap-1.5">
                <StepPill label="Remitente" state={currentStep === 'remitente' ? 'current' : remitente?.clienteId ? 'done' : 'pending'} />
                <StepPill label="Destinatario" state={currentStep === 'destinatario' ? 'current' : destinatario?.clienteId ? 'done' : 'pending'} />
                <StepPill label="Pagador" state={currentStep === 'pagador' ? 'current' : pagador?.clienteId ? 'done' : 'pending'} />
                <StepPill label="Remitos" state={currentStep === 'remitos' ? 'current' : filledItems.length ? 'done' : 'pending'} />
                <StepPill label="Emitir" state={currentStep === 'emitir' ? 'current' : 'pending'} />
              </div>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-12 gap-2">
            <div className="col-span-12 xl:col-span-4">
              <OperativeClientField
                label="Remitente"
                hotkey="F2"
                value={remitente}
                onChange={handleRemitenteChange}
                onAdvance={() => setFocusRequest({ type: 'client', role: 'destinatario', token: Date.now() })}
                inputRef={remitenteInputRef}
                recentClients={clientMemory.recent}
                lastClient={clientMemory.byRole?.remitente}
                status={currentStep === 'remitente' ? 'current' : remitente?.clienteId ? 'done' : 'pending'}
                searchCacheRef={searchCacheRef}
              />
            </div>

            <div className="col-span-12 xl:col-span-4">
              <OperativeClientField
                label="Destinatario"
                hotkey="F3"
                value={destinatario}
                onChange={handleDestinatarioChange}
                onAdvance={() => setFocusRequest({ type: 'client', role: 'pagador', token: Date.now() })}
                inputRef={destinatarioInputRef}
                recentClients={clientMemory.recent}
                lastClient={clientMemory.byRole?.destinatario}
                status={currentStep === 'destinatario' ? 'current' : destinatario?.clienteId ? 'done' : 'pending'}
                searchCacheRef={searchCacheRef}
              />
            </div>

            <div className="col-span-12 xl:col-span-4">
              <OperativeClientField
                label="Pagador"
                hotkey="Enter"
                value={pagador}
                onChange={handlePagadorChange}
                onAdvance={() => focusFirstOpenRow()}
                inputRef={pagadorInputRef}
                recentClients={clientMemory.recent}
                lastClient={clientMemory.byRole?.pagador}
                status={currentStep === 'pagador' ? 'current' : pagador?.clienteId ? 'done' : 'pending'}
                summaryPrefix={
                  pagadorSource === 'remitente'
                    ? 'Igual a remitente'
                    : pagadorSource === 'destinatario'
                      ? 'Igual a destinatario'
                      : null
                }
                quickActions={[
                  { label: 'Igual remitente', disabled: !remitente?.clienteId, onClick: () => assignPagadorFrom('remitente') },
                  { label: 'Igual destinatario', disabled: !destinatario?.clienteId, onClick: () => assignPagadorFrom('destinatario') },
                ]}
                searchCacheRef={searchCacheRef}
              />
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {HOTKEY_HINTS.map(hint => (
              <span
                key={`${hint.key}-${hint.label}`}
                className="rounded-full border border-[rgba(var(--border),0.42)] bg-[rgba(var(--bg),0.2)] px-2.5 py-1 text-[11px] text-[rgba(var(--muted),0.94)]"
              >
                <strong className="font-semibold text-[rgb(var(--fg))]">{hint.key}</strong> {hint.label}
              </span>
            ))}
          </div>
        </section>

        {(feedback || emisiones.length > 0) ? (
          <section
            className={clsx(
              cargasPanelClassName,
              'shrink-0 px-3 py-2.5',
              feedback?.type === 'error' || feedback?.type === 'warning'
                ? 'border-[rgba(var(--brand-700),0.32)] bg-[rgba(var(--brand),0.08)]'
                : null,
            )}
            aria-live="polite"
          >
            {feedback ? (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[rgb(var(--fg))]">{feedback.message}</p>
                  <p className="text-xs text-[rgba(var(--muted),0.92)]">
                    {feedback.type === 'error'
                      ? 'Corregi el punto marcado y segui.'
                      : feedback.type === 'warning'
                        ? 'La emisión quedó guardada. Revisá la impresora sin detener la siguiente carga.'
                        : 'La impresora queda lista para la siguiente carga.'}
                  </p>
                </div>

              </div>
            ) : null}
            {emisiones.length > 0 ? (
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-[rgba(var(--border),0.42)] pt-2">
                <p className="text-xs text-[rgba(var(--muted),0.92)]">
                  Lote <span className="font-semibold text-[rgb(var(--fg))]">{emisiones[0].codigoEtiqueta}</span>
                  {' · '}
                  {emisiones.length} etiqueta{emisiones.length === 1 ? '' : 's'} ({emisiones[0].bultoNro || 1}/{emisiones[0].bultosTotal || emisiones.length} a {emisiones.length}/{emisiones[0].bultosTotal || emisiones.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className={cargasButtonClassName} onClick={() => void printLote(emisiones, 'button:print-lote')}>
                    Imprimir lote (F9)
                  </button>
                  {lastLote ? (
                    <button type="button" className={cargasButtonClassName} onClick={emitirGuiaDesdeLote}>
                      Emitir guía con este lote (F10)
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={cargasButtonClassName}
                    onClick={() => navigate(`/cargas/etiquetas?loteId=${emisiones[0].loteId}`)}
                  >
                    Ver etiquetas
                  </button>
                  <button
                    type="button"
                    className={cargasButtonClassName}
                    onClick={() => emisiones.forEach(item => downloadZpl(item.codigoEtiqueta, item.zpl))}
                  >
                    Descargar ZPL
                  </button>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        <section className={clsx(cargasPanelClassName, 'flex min-h-0 flex-1 flex-col overflow-hidden')}>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[rgba(var(--border),0.42)] px-3 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-semibold text-[rgb(var(--fg))]">Remitos</h2>
              <span className="rounded-full border border-[rgba(var(--border),0.42)] bg-[rgba(var(--bg),0.22)] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgba(var(--muted),0.92)]">
                Igual a emisión de guía
              </span>
              <span className="rounded-full border border-[rgba(var(--border),0.42)] bg-[rgba(var(--bg),0.22)] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgba(var(--muted),0.92)]">
                Scanner listo
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs text-[rgba(var(--muted),0.92)]">
              <span>F4 enfoca remito</span>
              <span>Enter agrega remito</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
            <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-semibold">
              <span className="rounded-full bg-[rgba(var(--bg),0.24)] px-2 py-1 text-[rgb(var(--fg))]">Remitos {totals.cantidad}</span>
              <span className="rounded-full bg-[rgba(var(--bg),0.24)] px-2 py-1 text-[rgb(var(--fg))]">Bultos {totals.bultos}</span>
              <span className="rounded-full bg-[rgba(var(--bg),0.24)] px-2 py-1 text-[rgb(var(--fg))]">Kg {totals.kgReales}</span>
              <span className="rounded-full bg-[rgba(var(--bg),0.24)] px-2 py-1 text-[rgb(var(--fg))]">M3 {totals.m3}</span>
              <span className="rounded-full bg-[rgba(var(--bg),0.24)] px-2 py-1 text-[rgb(var(--fg))]">Val. decl. {formatCurrency(totals.valorDeclarado)}</span>
            </div>
          </div>

          <div className="px-3 pb-2">
            <div className="grid min-w-0 grid-cols-12 gap-2 rounded-2xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--bg),0.18)] p-2.5">
              <div className="col-span-6 md:col-span-2 lg:col-span-1">
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Pto vta</label>
                <input
                  ref={remitoPuntoVentaInputRef}
                  id="etiquetas-remito-punto-venta"
                  inputMode="numeric"
                  placeholder="0000"
                  value={remitoDraft.punto_venta}
                  onChange={event => handlePuntoVentaChange(event.target.value)}
                  onBlur={handleRemitoDraftBlur}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      remitoNumeroInputRef.current?.focus?.();
                    }
                  }}
                  className={denseInputClassName}
                />
              </div>

              <div className="col-span-6 md:col-span-4 lg:col-span-2">
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Número de remito</label>
                <input
                  ref={remitoNumeroInputRef}
                  id="etiquetas-numero-remito"
                  inputMode="numeric"
                  placeholder="00000000"
                  value={remitoDraft.numero_remito}
                  onChange={event => handleNumeroRemitoChange(event.target.value)}
                  onBlur={handleRemitoDraftBlur}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleAddRemito();
                    }
                  }}
                  className={denseInputClassName}
                />
                {remitoErrors.numero_remito ? <p className="mt-1 text-xs text-[rgb(var(--brand-700))]">{remitoErrors.numero_remito}</p> : null}
              </div>

              {[
                { key: 'bultos', label: 'Bultos', className: 'col-span-4 md:col-span-2 lg:col-span-1', id: 'etiquetas-bultos' },
                { key: 'kg_reales', label: 'Kg reales', className: 'col-span-4 md:col-span-2 lg:col-span-1' },
                { key: 'm3', label: 'M3', className: 'col-span-4 md:col-span-2 lg:col-span-1' },
                { key: 'kg_aforado', label: 'Kg aforado', className: 'col-span-6 md:col-span-2 lg:col-span-1' },
                { key: 'valor_declarado', label: 'Valor declarado', className: 'col-span-6 md:col-span-3 lg:col-span-2' },
              ].map(field => (
                <div key={field.key} className={field.className}>
                  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">{field.label}</label>
                  <input
                    id={field.id}
                    inputMode="decimal"
                    placeholder="0"
                    value={remitoDraft[field.key]}
                    onChange={event => handleDraftChange(field.key, event.target.value)}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleAddRemito();
                      }
                    }}
                    className={denseInputClassName}
                  />
                  {remitoErrors[field.key] ? <p className="mt-1 text-xs text-[rgb(var(--brand-700))]">{remitoErrors[field.key]}</p> : null}
                </div>
              ))}

              <div className="col-span-12 md:col-span-9 lg:col-span-3">
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Descripción</label>
                <>
                  <input
                    id="etiquetas-remito-descripcion"
                    list="etiquetas-remito-descripcion-options"
                    value={remitoDraft.descripcion}
                    onChange={event => handleDraftChange('descripcion', event.target.value)}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleAddRemito();
                      }
                    }}
                    placeholder="Bultos, cajas, pallets o texto libre"
                    className={denseInputClassName}
                  />
                  <datalist id="etiquetas-remito-descripcion-options">
                    {remitoDescriptionOptions.map(option => (
                      <option key={option} value={option} />
                    ))}
                  </datalist>
                </>
                {remitoErrors.descripcion ? <p className="mt-1 text-xs text-[rgb(var(--brand-700))]">{remitoErrors.descripcion}</p> : null}
              </div>
            </div>

            <div className="mt-2 flex justify-end">
              <button type="button" className={cargasButtonClassName} onClick={handleAddRemito}>
                Agregar remito
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 p-2 pt-0">
            <CargasTableShell className="h-full">
              <table className="min-w-full table-fixed text-left text-sm">
                <thead className="sticky top-0 z-10 bg-[rgb(var(--panel))]">
                  <tr className="border-b border-[rgba(var(--border),0.42)]">
                    <th className="w-12 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--muted),0.92)]">#</th>
                    <th className="w-16 px-2 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--muted),0.92)]">Pto</th>
                    <th className="w-24 px-2 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--muted),0.92)]">Remito</th>
                    <th className="w-16 px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--muted),0.92)]">Bultos</th>
                    <th className="w-20 px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--muted),0.92)]">Kg</th>
                    <th className="w-16 px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--muted),0.92)]">M3</th>
                    <th className="w-20 px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--muted),0.92)]">Aforado</th>
                    <th className="w-24 px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--muted),0.92)]">Val. decl.</th>
                    <th className="px-2 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--muted),0.92)]">Descripción</th>
                    <th className="w-44 px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--muted),0.92)]">Acciones</th>
                  </tr>
                </thead>

                <tbody>
                  {!filledItems.length ? (
                    <tr>
                      <td colSpan={10} className="px-3 py-4 text-sm text-[rgba(var(--muted),0.92)]">
                        Agregue al menos un remito para emitir la etiqueta.
                      </td>
                    </tr>
                  ) : null}
                  {filledItems.map((item, index) => {
                    const remitoParts = splitRemitoNumber(item.remito || item.numero_remito);

                    return (
                      <tr
                        key={item.id || `item-${index}`}
                        className={clsx(
                          'border-t border-[rgba(var(--border),0.32)] transition-colors',
                          highlightRemitoId && highlightRemitoId === item.id ? 'bg-[rgba(var(--brand),0.12)]' : null,
                        )}
                        onDoubleClick={() => startEditingRemito(index, item)}
                      >
                        <td className="px-2 py-1.5 text-center text-xs font-semibold text-[rgba(var(--muted),0.94)]">
                          {index + 1}
                        </td>
                        <td className="px-2 py-1.5">{remitoParts.puntoVenta || '0000'}</td>
                        <td className="px-2 py-1.5 font-semibold">
                          {editingRowIndex === index ? (
                            <input
                              value={editingRemitoDraft.numero_remito}
                              onChange={event => handleEditingDraftChange('numero_remito', event.target.value)}
                              className={denseInputClassName}
                            />
                          ) : (
                            remitoParts.numero || item.numero_remito
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {editingRowIndex === index ? <input value={editingRemitoDraft.bultos} onChange={event => handleEditingDraftChange('bultos', event.target.value)} className={denseInputClassName} /> : item.bultos}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {editingRowIndex === index ? <input value={editingRemitoDraft.kg_reales} onChange={event => handleEditingDraftChange('kg_reales', event.target.value)} className={denseInputClassName} /> : item.kg_reales}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {editingRowIndex === index ? <input value={editingRemitoDraft.m3} onChange={event => handleEditingDraftChange('m3', event.target.value)} className={denseInputClassName} /> : item.m3}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {editingRowIndex === index ? <input value={editingRemitoDraft.kg_aforado} onChange={event => handleEditingDraftChange('kg_aforado', event.target.value)} className={denseInputClassName} /> : item.kg_aforado}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {editingRowIndex === index ? <input value={editingRemitoDraft.valor_declarado} onChange={event => handleEditingDraftChange('valor_declarado', event.target.value)} className={denseInputClassName} /> : formatCurrency(item.valor_declarado)}
                        </td>
                        <td className="px-2 py-1.5">
                          {editingRowIndex === index ? (
                            <input value={editingRemitoDraft.descripcion} onChange={event => handleEditingDraftChange('descripcion', event.target.value)} className={denseInputClassName} />
                          ) : (
                            <span className="block truncate" title={item.descripcion}>{item.descripcion || 'Sin descripción'}</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <div className="inline-flex gap-1">
                            {editingRowIndex === index ? (
                              <>
                                <button type="button" className={cargasButtonClassName} onClick={saveEditingRemito}>Guardar</button>
                                <button type="button" className={cargasButtonClassName} onClick={cancelEditingRemito}>Cancelar</button>
                              </>
                            ) : (
                              <button type="button" className={cargasButtonClassName} onClick={() => startEditingRemito(index, item)}>Editar</button>
                            )}
                            <button type="button" className={cargasButtonClassName} onClick={() => duplicateRemito(index)}>Copiar</button>
                            <button type="button" className={cargasButtonClassName} onClick={() => removeRow(index)}>Borrar</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CargasTableShell>
          </div>
        </section>
      </CargasViewport>
    </CargasPage>
  );
}
